import React, { useCallback, useMemo } from 'react';
import config from '@config/index';
import { Alert, Icon, Text } from '@goodfynd/ui';
import Skeleton from '@mui/material/Skeleton';
import { getDateRangeDisplay } from '@utils/date-util';
import {
  calculatePercentChange,
  getAmountDisplay,
  getDisplayColor,
  getWholeNumber,
  renderPrice,
} from '@utils/number-util';

import TrendGraph from '../../../components/TrendGraph';
import { useOrdersOverview } from '../orders-overview-context/OrdersOverviewProvider';
import { OverviewCard } from '../overview-card';

import { StyledChart, StyledTrendGraph } from './styles';
import type { OrdersOverviewCardProps } from './types';

export function OrdersOverviewCard({
  amount = 0,
  amountLastPeriod = 0,
  amountType = 'number',
  suffixMillions = 'M',
  suffixThousands = 'K',
  trendData = [],
  trendDataCompare = [],
  ...props
}: OrdersOverviewCardProps) {
  const {
    dateEndCompare,
    dateStartCompare,
    trendHasError,
    trendIsFetching,
    xAxisLabelLeft,
    xAxisLabelRight,
  } = useOrdersOverview();

  const amountDisplay = useMemo(() => {
    if (amount === undefined || typeof amount !== 'number') {
      return '';
    }

    return getAmountDisplay(amount, {
      suffixMillions,
      suffixThousands,
    });
  }, [amount, suffixMillions, suffixThousands]);

  const currencyDisplay = useMemo(() => {
    switch (amountType) {
      case 'currency':
        return '$';

      default:
        return '';
    }
  }, [amountType]);

  const cents = useMemo(() => {
    if (amount === undefined || typeof amount !== 'number' || amount > 1000) {
      return 0;
    }

    return getWholeNumber((amount - Math.floor(amount)) * 100);
  }, [amount]);

  const deltaPercentage = useMemo(
    () => calculatePercentChange(amountLastPeriod || 0, amount || 0),
    [amount, amountLastPeriod]
  );

  const renderAmount = useCallback(() => {
    if (trendHasError) {
      return <Alert status="error">An error has occurred</Alert>;
    }

    if (!amountDisplay) {
      return null;
    }

    return (
      <>
        <Text as="heading1" title={renderPrice(amount)}>
          {!!currencyDisplay && (
            <Text
              as="heading3"
              style={{ position: 'relative', top: 3 }}
              tag="sup"
            >
              {currencyDisplay}
            </Text>
          )}

          {amountDisplay}

          {cents > 0 && (
            <Text
              as="heading3"
              style={{ position: 'relative', top: 3 }}
              tag="sup"
            >
              {cents.toString().padStart(2, '0')}
            </Text>
          )}
        </Text>

        <Text
          as="caption"
          tag="span"
          title={renderPrice(amountLastPeriod)}
          style={{
            alignItems: 'center',
            color: `$${getDisplayColor(deltaPercentage)}`,
            display: 'flex',
          }}
        >
          <Icon
            css={{ marginBottom: 2 }}
            name={deltaPercentage >= 0 ? 'arrow_up' : 'arrow_down'}
            size={16}
          />
          {deltaPercentage >= 0 ? config.unicode.plus : config.unicode.minus}
          {getAmountDisplay(Math.abs(deltaPercentage), { mantissa: 1 })}
          %&nbsp;vs&nbsp;
          {getDateRangeDisplay(dateStartCompare, dateEndCompare)}
        </Text>
      </>
    );
  }, [
    amount,
    amountDisplay,
    amountLastPeriod,
    cents,
    currencyDisplay,
    dateEndCompare,
    dateStartCompare,
    deltaPercentage,
    trendHasError,
  ]);

  return (
    <OverviewCard {...props}>
      {trendIsFetching ? (
        <>
          <Text as="heading1" tag="div">
            <Skeleton width={131} />
          </Text>
          <Text as="caption" tag="div">
            <Skeleton width={131} />
          </Text>

          <Skeleton height={165} sx={{ marginTop: 4 }} />
        </>
      ) : (
        <>
          {renderAmount()}

          <StyledChart>
            <StyledTrendGraph
              data={trendData}
              xAxisLabelLeft={xAxisLabelLeft}
              xAxisLabelRight={xAxisLabelRight}
            />

            <StyledChart>
              <TrendGraph
                data={trendDataCompare}
                lineColors={['GreenLighter']}
                lineWidth={1}
              />
            </StyledChart>
          </StyledChart>
        </>
      )}
    </OverviewCard>
  );
}
